// @wc-ignore-file
// (No user-facing copy lives here — only column names and an RPC name. wuchale
// would otherwise machine-translate those data literals; see CLAUDE.md.)
import { supabase } from "@/lib/supabase";

/**
 * CR-51: "when did I last open this container?".
 *
 * Backed by `user_container_access`, whose RLS is self-scoped, so neither call
 * below can read or write another user's row — the explicit `user_id` filter on
 * the read is a cache-honesty measure (the query key names a user, so the query
 * should too), not the security boundary.
 */
export type ContainerAccessRow = {
  container_id: string;
  last_accessed_at: string;
};

/**
 * Stamp "the calling user just opened this container".
 *
 * Rejects containers the caller cannot access with a constant error, so a
 * failure here says nothing about whether the id exists.
 */
export async function recordContainerAccess(containerId: string): Promise<void> {
  const { error } = await supabase.rpc("record_container_access", {
    p_container_id: containerId,
  });

  if (error) throw error;
}

/**
 * How many access rows the sidebar fetches. The table grows one row per
 * container the user ever opened, but ranking ~15 sidebar items only needs the
 * recent tail — anything older degrades gracefully to the updated_at fallback.
 */
const ACCESS_ROWS_LIMIT = 200;

/**
 * The containers this user opened most recently, most recent first (capped at
 * ACCESS_ROWS_LIMIT).
 *
 * Unfiltered by project on purpose: the sidebar needs a lookup table keyed by
 * container id, and one query sliced per section client-side beats a query per
 * section.
 */
export async function listContainerAccess(userId: string): Promise<ContainerAccessRow[]> {
  const { data, error } = await supabase
    .from("user_container_access")
    .select("container_id, last_accessed_at")
    .eq("user_id", userId)
    .order("last_accessed_at", { ascending: false })
    .limit(ACCESS_ROWS_LIMIT);

  if (error) throw error;
  return data ?? [];
}
